import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import akhiratLogo from "../../assets/images/akhirat-logo.png";
import { useCallback, useEffect, useRef, useState } from "react";
import LanguageSwitcher from "../LanguageSwitcher";

/* Small inline icon set — 1.7px stroke, geometric, matches the brand's restraint. */
const ICONS = {
  services: (
    <path d="M12 3c3 4 6 6.5 6 10a6 6 0 1 1-12 0c0-3.5 3-6 6-10Z" />
  ),
  packages: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3" />
      <path d="M4 10h16M10 10v10" />
    </>
  ),
  process: (
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8.2 7.6C13 10 14 14 15.8 16.4" />
    </>
  ),
  guides: (
    <path d="M5 4h11l3 3v13H5zM9 4v5h6M9 20v-5h6" />
  ),
  infaq: (
    <path d="M12 20s-7-4.4-7-9.3C5 7.6 7.2 6 9.4 6c1.2 0 2 .5 2.6 1.3C12.6 6.5 13.4 6 14.6 6 16.8 6 19 7.6 19 10.7c0 4.9-7 9.3-7 9.3Z" />
  ),
  about: (
    <>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.2-3.4 3.9-5 7-5s5.8 1.6 7 5" />
    </>
  ),
  testimonials: (
    <path d="M12 3l2.5 5.5L20 9l-4 4 1 6-5-2.8L7 19l1-6-4-4 5.5-.5Z" />
  ),
};

const PanelIcon = ({ name }) => (
  <span className="flex-none w-[34px] h-[34px] rounded-lg grid place-items-center bg-[#F4F1E9] text-[#0A5C26]">
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {ICONS[name]}
    </svg>
  </span>
);

const Spark = () => (
  <svg className="absolute top-1.5 right-2 w-1.5 h-1.5 text-[#C2A14D]" viewBox="0 0 8 8" fill="currentColor" aria-hidden="true">
    <path d="M4 0l1 3 3 1-3 1-1 3-1-3-3-1 3-1z" />
  </svg>
);

function Navbar() {
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState(null); // 'services' | 'about' | 'resources' | null
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navRef = useRef(null);

  // Brand wordmark in the drawer: staging (ekubur.my / staging run.app host) shows
  // EKUBUR; production (akhirat.co) and anything else defaults to AKHIRAT.
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  const drawerBrand = /akhirat/.test(host) || !/ekubur/.test(host) ? "AKHIRAT" : "EKUBUR";

  const handleScroll = useCallback((target) => {
    const section = document.getElementById(target);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
      setOpenMenu(null);
      setDrawerOpen(false);
    }
  }, []);

  // Close menus on outside click and Escape.
  useEffect(() => {
    function handleClickOutside(e) {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenMenu(null);
    }
    function handleKey(e) {
      if (e.key === "Escape") {
        setOpenMenu(null);
        setDrawerOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Lock body scroll while the drawer is open.
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const toggleMenu = (key) => setOpenMenu((cur) => (cur === key ? null : key));

  /* ---- dropdown building blocks ---- */

  const PanelItem = ({ icon, title, desc, onClick, disabled = false, soon = false }) => (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : undefined}
      className={`w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-xl transition-colors animate-slide-up ${
        disabled ? "cursor-not-allowed opacity-40" : "hover:bg-[#EAF4EC]"
      }`}
    >
      <PanelIcon name={icon} />
      <span className="min-w-0">
        {/* Title on its own line, description stacked beneath it. */}
        <span className="block font-display text-[15px] tracking-wide text-gray-900">
          {title}
          {soon && (
            <span className="ml-2 align-middle inline-block px-1.5 py-px border border-[#C2A14D] text-[#8a6d1d] rounded-full text-[9px] font-bold tracking-[0.14em]">
              {t("navbar.soon")}
            </span>
          )}
        </span>
        <span className="block text-[12.5px] leading-snug text-gray-500 mt-0.5">{desc}</span>
      </span>
    </button>
  );

  const DropButton = ({ id, label }) => (
    <button
      type="button"
      aria-expanded={openMenu === id}
      aria-haspopup="true"
      onClick={() => toggleMenu(id)}
      className={`flex items-center gap-1.5 px-3.5 py-2.5 rounded-[10px] text-[14.5px] font-medium transition-colors ${
        openMenu === id ? "bg-[#EAF4EC] text-[#0A5C26]" : "text-gray-700 hover:bg-[#EAF4EC] hover:text-[#0A5C26]"
      }`}
    >
      {label}
      <svg
        className={`w-3.5 h-3.5 transition-transform duration-300 ${openMenu === id ? "rotate-180 text-[#0A5C26]" : "text-gray-400"}`}
        viewBox="0 0 16 16" fill="none"
      >
        <path d="M3.5 6l4.5 4.5L12.5 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
  );

  const servicesMega = (
    <div className="absolute left-0 top-full mt-2.5 w-[620px] before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 bg-white border border-gray-200 rounded-2xl shadow-2xl p-3.5 z-50">
      <div className="absolute top-0 left-4 right-4 h-0.5 rounded-b bg-gradient-to-r from-[#C2A14D] to-transparent" />
      <div className="grid grid-cols-3 gap-2">
        <PanelItem icon="services" title={t("navbar.services")} desc={t("navbar.servicesDesc")} onClick={() => handleScroll("services")} />
        <PanelItem icon="packages" title={t("navbar.packages")} desc={t("navbar.packagesDesc")} onClick={() => handleScroll("packages")} />
        <PanelItem icon="process" title={t("navbar.process")} desc={t("navbar.processDesc")} onClick={() => handleScroll("process")} />
      </div>
      <div className="mt-2.5 flex items-center justify-between gap-3 px-3.5 py-3 rounded-xl border border-dashed border-[#0D7330]/25 bg-gradient-to-r from-[#EAF4EC] to-transparent">
        <span className="text-[13px] text-gray-600">{t("navbar.notSure")}</span>
        <button type="button" onClick={() => handleScroll("contact")} className="flex-none inline-flex items-center gap-1.5 text-[13px] font-bold text-[#0A5C26] hover:gap-2.5 transition-all">
          {t("navbar.talkToUs")}
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 8h9M8.5 4.5 12 8l-3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
      </div>
    </div>
  );

  const aboutPanel = (
    <div className="absolute left-0 top-full mt-2.5 w-[300px] before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 bg-white border border-gray-200 rounded-2xl shadow-2xl p-2.5 z-50">
      <div className="absolute top-0 left-4 right-4 h-0.5 rounded-b bg-gradient-to-r from-[#C2A14D] to-transparent" />
      <PanelItem icon="about" title={t("navbar.about")} desc={t("navbar.aboutDesc")} disabled soon />
      <PanelItem icon="testimonials" title={t("navbar.testimonials")} desc={t("navbar.testimonialsDesc")} onClick={() => handleScroll("testimonials")} />
    </div>
  );

  const resourcesPanel = (
    <div className="absolute left-0 top-full mt-2.5 w-[300px] before:content-[''] before:absolute before:-top-3 before:left-0 before:right-0 before:h-3 bg-white border border-gray-200 rounded-2xl shadow-2xl p-2.5 z-50">
      <div className="absolute top-0 left-4 right-4 h-0.5 rounded-b bg-gradient-to-r from-[#C2A14D] to-transparent" />
      <PanelItem icon="guides" title={t("navbar.guides")} desc={t("navbar.guidesDesc")} onClick={() => handleScroll("guides")} />
      <PanelItem icon="infaq" title={t("navbar.infaq")} desc={t("navbar.infaqDesc")} onClick={() => handleScroll("infaq")} />
    </div>
  );

  /* ---- drawer (mobile) ---- */

  const DrawerGroup = ({ title, children }) => (
    <div className="py-4">
      <div className="flex items-center gap-2.5 mb-1.5 font-display text-[12.5px] tracking-[0.24em] uppercase text-[#C2A14D]">
        {title}
        <span className="flex-1 h-px bg-[#C2A14D]/25" />
      </div>
      {children}
    </div>
  );

  const DrawerItem = ({ label, onClick, to, disabled = false, soon = false }) => {
    const inner = (
      <>
        <span>
          {label}
          {soon && (
            <span className="ml-2 inline-block px-1.5 py-px border border-[#C2A14D]/60 text-[#C2A14D] rounded-full text-[9px] font-bold tracking-[0.14em]">
              {t("navbar.soon")}
            </span>
          )}
        </span>
        {!disabled && (
          <span className="text-[#C2A14D] opacity-0 -translate-x-1.5 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0">→</span>
        )}
      </>
    );
    const cls = `group w-full flex items-center justify-between py-2.5 px-1 text-[15.5px] text-[#F2EFE6] rounded-lg transition-all ${
      disabled ? "opacity-40 cursor-not-allowed" : "hover:pl-2.5 hover:text-white"
    }`;
    if (disabled) {
      return <button type="button" aria-disabled="true" className={cls}>{inner}</button>;
    }
    if (to) {
      return <Link to={to} onClick={onClick} className={cls}>{inner}</Link>;
    }
    return <button type="button" onClick={onClick} className={cls}>{inner}</button>;
  };

  return (
    <div ref={navRef} className="navbar relative flex justify-between items-center py-3 px-4 md:px-12 md:py-4 bg-white border-b border-gray-200 shadow-sm z-40">
      <Link to="/" className="inline-flex items-center">
        <img src={akhiratLogo} alt="Akhirat" className="h-[56px] md:h-[60px]" />
      </Link>

      {/* ---------- desktop ---------- */}
      <div className="hidden md:flex items-center gap-3">
        <ul className="flex items-center">
          {/* Services — descriptive panel */}
          <li className="relative" onMouseEnter={() => setOpenMenu("services")} onMouseLeave={() => setOpenMenu(null)}>
            <DropButton id="services" label={t("navbar.services")} />
            {openMenu === "services" && servicesMega}
          </li>

          {/* About — items greyed out for now */}
          <li className="relative" onMouseEnter={() => setOpenMenu("about")} onMouseLeave={() => setOpenMenu(null)}>
            <DropButton id="about" label={t("navbar.about")} />
            {openMenu === "about" && aboutPanel}
          </li>

          {/* Resources */}
          <li className="relative" onMouseEnter={() => setOpenMenu("resources")} onMouseLeave={() => setOpenMenu(null)}>
            <DropButton id="resources" label={t("navbar.resources")} />
            {openMenu === "resources" && resourcesPanel}
          </li>

          <li>
            <Link
              to="/claims/inheritance"
              className="relative inline-flex items-center px-3.5 py-2.5 rounded-[10px] text-[14.5px] font-medium text-[#0A5C26] hover:bg-[#EAF4EC] transition-colors"
            >
              {t("navbar.claim")}
              <Spark />
            </Link>
          </li>
          <li>
            <button
              type="button"
              onClick={() => handleScroll("contact")}
              className="px-3.5 py-2.5 rounded-[10px] text-[14.5px] font-medium text-gray-700 hover:bg-[#EAF4EC] hover:text-[#0A5C26] transition-colors"
            >
              {t("navbar.contact")}
            </button>
          </li>
        </ul>

        <div className="flex items-center gap-3 ml-3">
          <LanguageSwitcher />
          <Link
            to="/login"
            className="px-4 py-2 text-[#0D7330] border-[1.5px] border-[#0D7330] rounded-[10px] text-sm font-semibold hover:bg-[#0D7330] hover:text-white transition-colors"
          >
            {t("navbar.login")}
          </Link>
          <Link
            to="/register"
            className="px-4 py-2 bg-[#0D7330] text-white rounded-[10px] text-sm font-semibold shadow-lg shadow-[#0D7330]/25 hover:bg-[#0a5c26] transition-colors"
          >
            {t("navbar.getStarted")}
          </Link>
        </div>
      </div>

      {/* ---------- mobile trigger ---------- */}
      <div className="md:hidden flex items-center gap-3">
        <LanguageSwitcher />
        <button
          type="button"
          aria-expanded={drawerOpen}
          aria-controls="mobile-drawer"
          onClick={() => setDrawerOpen(true)}
          className="flex items-center gap-2 px-3.5 py-2 border-[1.5px] border-gray-900 rounded-full text-[13px] font-semibold text-gray-900"
        >
          {t("navbar.menu")}
          <span className="grid gap-[3.5px]">
            <i className="block w-[15px] h-[1.6px] bg-current rounded" />
            <i className="block w-[15px] h-[1.6px] bg-current rounded" />
            <i className="block w-[15px] h-[1.6px] bg-current rounded" />
          </span>
        </button>
      </div>

      {/* ---------- mobile drawer ---------- */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${
          drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-[#10231A]/45 backdrop-blur-[3px]" onClick={() => setDrawerOpen(false)} />
        <aside
          id="mobile-drawer"
          role="dialog"
          aria-modal="true"
          aria-label="Menu"
          className={`absolute top-0 right-0 bottom-0 w-[86%] max-w-[400px] flex flex-col bg-gradient-to-b from-[#0E2018] via-[#123024] to-[#0E2018] text-[#F2EFE6] shadow-[-30px_0_70px_-30px_rgba(16,35,26,0.55)] transition-transform duration-500 ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* subtle eight-point star pattern */}
          <div
            className="absolute inset-0 opacity-5 pointer-events-none"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Cg fill='none' stroke='%23C2A14D' stroke-width='1'%3E%3Cpath d='M60 8 L70 50 L112 60 L70 70 L60 112 L50 70 L8 60 L50 50 Z'/%3E%3C/g%3E%3C/svg%3E\")",
              backgroundSize: "130px",
            }}
          />
          <div className="relative flex items-center justify-between px-6 py-5 border-b border-white/15">
            {/* Brand wordmark: staging (ekubur.*) shows EKUBUR; production (akhirat.co) and
                any other host default to AKHIRAT. */}
            <span className="font-display tracking-[0.16em] text-[16px]">{drawerBrand}</span>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setDrawerOpen(false)}
              className="w-9 h-9 rounded-full grid place-items-center border border-white/25 hover:bg-white/10 hover:rotate-90 transition-all duration-300"
            >
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 3l10 10M13 3 3 13" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /></svg>
            </button>
          </div>

          <div className="relative flex-1 overflow-y-auto px-6 pb-6">
            <DrawerGroup title={t("navbar.services")}>
              <DrawerItem label={t("navbar.services")} onClick={() => handleScroll("services")} />
              <DrawerItem label={t("navbar.packages")} onClick={() => handleScroll("packages")} />
              <DrawerItem label={t("navbar.process")} onClick={() => handleScroll("process")} />
            </DrawerGroup>
            <DrawerGroup title={t("navbar.resources")}>
              <DrawerItem label={t("navbar.guides")} onClick={() => handleScroll("guides")} />
              <DrawerItem label={t("navbar.infaq")} onClick={() => handleScroll("infaq")} />
            </DrawerGroup>
            <DrawerGroup title={t("navbar.about")}>
              <DrawerItem label={t("navbar.about")} disabled soon />
              <DrawerItem label={t("navbar.testimonials")} onClick={() => handleScroll("testimonials")} />
            </DrawerGroup>
            <DrawerGroup title={t("navbar.direct")}>
              <DrawerItem label={t("navbar.claim")} to="/claims/inheritance" onClick={() => setDrawerOpen(false)} />
              <DrawerItem label={t("navbar.contact")} onClick={() => { setDrawerOpen(false); setTimeout(() => handleScroll("contact"), 350); }} />
            </DrawerGroup>
          </div>

          <div className="relative px-6 py-4 border-t border-white/15">
            <Link
              to="/login"
              onClick={() => setDrawerOpen(false)}
              className="block w-full text-center px-4 py-2.5 mb-2.5 border border-white/35 rounded-[10px] text-sm font-semibold text-[#F2EFE6] hover:bg-white/10 transition-colors"
            >
              {t("navbar.login")}
            </Link>
            <Link
              to="/register"
              onClick={() => setDrawerOpen(false)}
              className="block w-full text-center px-4 py-2.5 bg-[#C2A14D] text-[#171509] rounded-[10px] text-sm font-bold hover:bg-[#D4B45F] transition-colors"
            >
              {t("navbar.getStarted")}
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}

export default Navbar;
